import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useQueryGetActiveTestSessions, { invalidateTestSessionQueries } from '@/api/user/getActiveTestSessions.ts'
import TestModeToast from '@/component/testMode/TestModeToast.tsx'

const EXPIRY_GRACE_MS = 1500

const TestSessionManager = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const { data: sessions } = useQueryGetActiveTestSessions()
    const expiredIdsRef = useRef(new Set<number>())

    useEffect(() => {
        const pending = (sessions ?? []).filter(
            session => !expiredIdsRef.current.has(session.id) && !Number.isNaN(Date.parse(session.endsAt))
        )
        if (pending.length === 0) return

        const nextEnd = Math.min(...pending.map(session => Date.parse(session.endsAt)))
        const timer = window.setTimeout(() => {
            const now = Date.now()
            const expired = pending.filter(session => Date.parse(session.endsAt) <= now)
            expired.forEach(session => expiredIdsRef.current.add(session.id))
            expired
                .filter(session => session.isMine)
                .forEach(session =>
                    toast.info(
                        <TestModeToast
                            title={t('testMode.toast.expired.title')}
                            description={t('testMode.toast.ended.desc', { name: session.protecteeName })}
                        />
                    )
                )
            invalidateTestSessionQueries(queryClient)
        }, Math.max(0, nextEnd - Date.now()) + EXPIRY_GRACE_MS)

        return () => window.clearTimeout(timer)
    }, [sessions, queryClient, t])

    return null
}

export default TestSessionManager
